// Sentry initialization — no-op when REACT_APP_SENTRY_DSN is empty.
// Centralized so we can add new integrations in one place.
//
// @sentry/react is ~480 KB, so it's only downloaded (as a separate chunk)
// when a DSN is configured. Without a DSN nothing is downloaded at all.
import React from 'react';

const DSN = process.env.REACT_APP_SENTRY_DSN || '';

let sentry = null; // the loaded @sentry/react module, once ready

if (DSN) {
  import('@sentry/react')
    .then((Sentry) => {
      Sentry.init({
        dsn: DSN,
        environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'production',
        release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
        // Conservative defaults to keep free-tier quota happy
        tracesSampleRate: 0.0,
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 0.0,
        // Avoid PII leakage
        sendDefaultPii: false,
      });
      sentry = Sentry;
    })
    .catch((e) => {
      // Never block app boot due to telemetry failure
      // eslint-disable-next-line no-console
      console.warn('Sentry init failed', e);
    });
}

// Shows `fallback` if rendering crashes, and reports the error to Sentry when
// it's loaded (replaces Sentry.ErrorBoundary so it works without Sentry).
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    try {
      sentry?.captureException(error, { contexts: { react: { componentStack: info?.componentStack } } });
    } catch { /* telemetry must never throw */ }
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
